import _ from 'lodash';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { ICurrency } from 'constants/business-plan.constants';
import { initialMessage, userMessage } from 'constants/ui.constants';
import { pendingRequestsTypes } from 'constants/requests.constants';
import { BankAccountStatus } from 'constants/customer-account.constants';

interface IPaginationState {
	hasMore: boolean;
	total: number;
}

interface IGlobalState {
	currencies: ICurrency[] | null;
	companies: Record<string, any> | undefined;
	companiesExpendedData: any[];
	companiesExpendedPagination: IPaginationState;
	countries: any[] | null;
	industries: any[] | null;
	paymentsPurpose: any[] | null;
	bankList: any[] | null;
	error: userMessage;
	altshulerAccountList: any[] | null;
	loading: boolean;
	version: string;
}

const initialState: IGlobalState = {
	currencies: null,
	companies: undefined,
	companiesExpendedData: [],
	companiesExpendedPagination: { hasMore: false, total: 0 },
	countries: null,
	industries: null,
	paymentsPurpose: null,
	bankList: null,
	error: initialMessage,
	altshulerAccountList: null,
	loading: false,
	version: 'n/a',
};

const getItemsWithCommonProperties = (items: any[], nameProperty = 'name', valueProperty = 'id') =>
	_.map(items, (item: any) => ({
		...item,
		label: item[nameProperty] ? _.trim(item[nameProperty]) : '',
		value: _.get(item, valueProperty, 0),
	}));

export const globalSlice = createSlice({
	name: 'global',
	initialState,
	reducers: {
		setCurrencies: (state, { payload }) => {
			state.currencies = getItemsWithCommonProperties(payload);
		},
		setCompanies: (state, { payload }) => {
			state.companies = getItemsWithCommonProperties(payload, 'hebrew_name');
		},
		setCompaniesExpendedData: (state, { payload }) => {
			state.companiesExpendedData = getItemsWithCommonProperties(payload.data);
			state.companiesExpendedPagination = {
				hasMore: payload.pagination?.hasMore ?? false,
				total: payload.pagination?.total ?? 0,
			};
		},
		setCountries: (state, { payload }) => {
			state.countries = getItemsWithCommonProperties(payload);
		},
		setIndustries: (state, { payload }) => {
			state.industries = getItemsWithCommonProperties(payload, 'nameHe');
		},
		setPaymentsPurpose: (state, { payload }) => {
			state.paymentsPurpose = getItemsWithCommonProperties(payload);
		},
		setBankList: (state, { payload }) => {
			state.bankList = getItemsWithCommonProperties(payload, 'bankName');
		},
		setError: (state, action: PayloadAction<userMessage>) => {
			state.error = action.payload;
		},
		setAltshulerAccountList: (state, { payload }) => {
			state.altshulerAccountList = _.map(payload, (altshulerAccount) => ({
				...altshulerAccount,
				bankName: altshulerAccount.bank.bankName,
			}));
		},
		setLoading: (state, { payload }) => {
			state.loading = payload;
		},
		setVersion: (state, { payload }) => {
			state.version = payload;
		},
	},
});

export const getCurrencies = (state: any) => {
	return state.global.currencies;
};

export const getCompanies = (state: any) => {
	return state.global.companies;
};

export const getCountries = (state: any) => {
	return state.global.countries;
};

export const getIndustries = (state: any) => {
	return state.global.industries;
};

export const getPaymentsPurpose = (state: any) => {
	return state.global.paymentsPurpose;
};

export const getBankList = (state: any) => {
	return state.global.bankList;
};

export const getError = (state: any) => {
	return state.global.error;
};

export const getAltshulerAccountList = (state: any) => {
	return state.global.altshulerAccountList;
};
export const isLoading = (state: any) => state.content.loading;

export const getVersion = (state: any) => {
	return state.global.version;
};

export const getOptionsDependenciesForCreate = (type: pendingRequestsTypes) => (state: any) => {
	switch (type) {
		case pendingRequestsTypes.BALANCES:
			return {
				'approvedBy.id': state?.customerAccount?.companyDashboard?.contacts
					?.filter((contact: any) => contact?.oid !== undefined)
					?.map((contact: any) => {
						return {
							// ...contact,
							label: `${contact?.firstNameEnglish || contact?.firstName} ${
								contact?.lastNameEnglish || contact?.lastName
							}`,
							value: contact.id,
						};
					}),
				'currency.id': [
					{
						label: state?.global?.currencies?.find((currency: any) => currency.code === 'ILS').name,
						value: state?.global?.currencies?.find((currency: any) => currency.code === 'ILS').id,
					},
				],
			};
		case pendingRequestsTypes.WITHDRAWAL_SUPPORT:
			let currency = state.global.currencies;
			let companyBankAccountId = [];
			if (state.customerAccount?.companyDashboard?.balances) {
				currency = state.customerAccount?.companyDashboard?.balances.map((balance: any) => {
					return {
						...balance,
						value: Number(balance.currencyId),
						label: `${balance?.currency?.name}`,
						disabled: true,
					};
				});
			}
			if (state.customerAccount?.companyDashboard?.bankAccounts) {
				companyBankAccountId = state.customerAccount?.companyDashboard?.bankAccounts.map((bankAccount: any) => {
					return {
						...bankAccount,
						value: bankAccount.id,
						label: `${bankAccount?.bankName}  ${bankAccount?.accountNumber}/${bankAccount?.bankBranch}`,
					};
				});
			}
			return {
				approvedBy: state?.customerAccount?.companyDashboard?.contacts
					?.filter((contact: any) => {
						return contact?.oid !== undefined;
					})
					?.map((contact: any) => {
						return {
							...contact,
							value: contact.id,
							label: `${contact?.firstNameEnglish || contact?.firstName} ${
								contact?.lastNameEnglish || contact?.lastName
							}`,
						};
					}),
				currency,
				companyBankAccountId,
			};
		case pendingRequestsTypes.BANK_ACCOUNTS:
			return {
				'bankName.bankCode': state.global.bankList,
			};
		case pendingRequestsTypes.CONTACTS:
			return { birthCountryId: state.global.countries };
		default:
			return {};
	}
};

export const getCustomDependenciesForCreate = (type: pendingRequestsTypes) => (state: any) => {
	switch (type) {
		case pendingRequestsTypes.BALANCES:
			return {
				'bankAccount.id': {
					accounts: state?.customerAccount?.companyDashboard?.bankAccounts?.filter(
						(account: any) => account.status == BankAccountStatus.Completed
					),
				},
				altshulerAccountId: {
					accounts: state.global.altshulerAccountList,
				},
			};
		default:
			return {};
	}
};

export const getInitialFormValuesForCreate = (type: pendingRequestsTypes) => (state: any) => {
	switch (type) {
		case pendingRequestsTypes.BALANCES:
			return {
				'currency.id': state?.global?.currencies?.find((currency: any) => currency.code === 'ILS').id,
				createdAt: new Date(),
			};
		case pendingRequestsTypes.WITHDRAWAL_SUPPORT:
			return {
				approveTitle: true,
				createdAt: new Date(),
			};
		default:
			return {};
	}
};

export const getOptionsDependenciesForApprove = (type: pendingRequestsTypes, item?: any) => (state: any) => {
	switch (type) {
		case pendingRequestsTypes.BALANCES:
			return {
				currencyId: state.global.currencies,
			};
		case pendingRequestsTypes.WITHDRAWAL_SUPPORT:
			let currency = state.global.currencies;
			let companyBankAccountId = [];
			if (state.customerAccount?.companyDashboard?.balances) {
				currency = state.customerAccount?.companyDashboard?.balances.map((balance: any) => {
					return {
						...balance,
						value: Number(balance.currencyId),
						label: `${balance?.currency?.name}`,
						disabled: true,
					};
				});
			}
			if (state.customerAccount?.companyDashboard?.bankAccounts) {
				companyBankAccountId = state.customerAccount?.companyDashboard?.bankAccounts.map((bankAccount: any) => {
					return {
						...bankAccount,
						value: bankAccount.id,
						label: `${bankAccount?.bankName}  ${bankAccount?.accountNumber}/${bankAccount?.bankBranch}`,
					};
				});
			}
			return {
				currency,
				companyBankAccountId,
			};
		case pendingRequestsTypes.BANK_ACCOUNTS:
			return {
				'bankName.bankCode': getItemsWithCommonProperties(state.global.bankList, 'bankName', 'bankCode'),
			};
		case pendingRequestsTypes.PAYMENTS:
			return {
				'beneficiary.bankAccountCountryId': state.global.countries,
			};
		case pendingRequestsTypes.BENEFICIARIES:
			return {
				currencyId: state.global.currencies,
			};
		case pendingRequestsTypes.CONTACTS:
			return {
				countryId: state.global.countries,
			};

		default:
			return {};
	}
};

export const getCustomDependenciesForApprove = (type: pendingRequestsTypes) => (state: any) => {
	switch (type) {
		case pendingRequestsTypes.BALANCES:
			return {
				altshulerAccountId: {
					accounts: state.global.altshulerAccountList,
				},
			};
		default:
			return {};
	}
};

export const getCompaniesWithExpendedData = (state: any) => {
	return state.global.companiesExpendedData;
};

export const getCompaniesExpendedPagination = (state: any) => {
	return state.global.companiesExpendedPagination;
};

export const {
	setCurrencies,
	setCompanies,
	setCompaniesExpendedData,
	setCountries,
	setIndustries,
	setPaymentsPurpose,
	setBankList,
	setError,
	setAltshulerAccountList,
	setLoading,
	setVersion,
} = globalSlice.actions;

export default globalSlice.reducer;
